import { Suspense, lazy, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoadingSpinner from "./components/ui/loading-spinner";
import { supabase } from "@/integrations/supabase/client";

import Login from "./pages/Login";
import PasswordReset from "./pages/PasswordReset";
import RequestPasswordReset from "./components/auth/RequestPasswordReset";

const Index = lazy(() => import("./pages/index"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const Inbox = lazy(() => import("./pages/Inbox"));
const VBHLogin = lazy(() => import("./pages/VBHLogin"));
const VBHDashboard = lazy(() => import("./pages/VBHDashboard"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const iconsToPreload = [
  "/lovable-uploads/9d04f9af-a06a-407e-ad93-8dc92297de24.png",
  "/lovable-uploads/e43bcc8b-f524-4f5d-9082-c45d4c516e18.png",
  "/lovable-uploads/443806e4-19c2-42ec-a991-53a10d195914.png",
  "/lovable-uploads/562e9d9b-e350-463d-84ad-cd5ab041efd6.png",
  "/lovable-uploads/c1843206-ee02-455d-a95f-f840eb3616e5.png",
  "/lovable-uploads/de8c24df-24f0-4169-9efa-a50353f4c388.png"
];

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsAdmin(session?.user?.user_metadata?.role === 'admin');
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setIsAdmin(session?.user?.user_metadata?.role === 'admin');
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // If this is an admin-only route and user is not an admin
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // If this is the regular dashboard and user is an admin
  if (!adminOnly && isAdmin && window.location.pathname === '/dashboard') {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);
  const [elementsStable, setElementsStable] = useState(false);
  const [dynamicImagesLoaded, setDynamicImagesLoaded] = useState(false);

  useEffect(() => {
    // Track font loading
    document.fonts.ready.then(() => {
      setFontsLoaded(true);
    });

    // Set up mutation observer for dynamic images
    const mutationObserver = new MutationObserver((mutations) => {
      const newImages: HTMLImageElement[] = [];
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement) {
            const images = node.getElementsByTagName('img');
            newImages.push(...Array.from(images));
          }
        });
      });

      if (newImages.length > 0) {
        Promise.all<void>(
          newImages.map(img => 
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve();
              } else {
                img.onload = () => resolve();
                img.onerror = () => resolve(); // Count failed images as loaded
              }
            })
          )
        ).then(() => {
          setDynamicImagesLoaded(true);
        });
      } else {
        setDynamicImagesLoaded(true);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Track element stability
    const resizeObserver = new ResizeObserver((entries) => {
      const heroContainer = document.querySelector('.hero-container');
      const heroImage = document.querySelector('.hero-image');
      
      if (heroContainer && heroImage) {
        const isStable = entries.every(entry => {
          const previousRect = entry.contentRect;
          const currentRect = entry.target.getBoundingClientRect();
          return (
            previousRect.width === currentRect.width &&
            previousRect.height === currentRect.height
          );
        });
        
        if (isStable) {
          setElementsStable(true);
        }
      }
    });

    // Observe hero elements once they exist
    const observeHeroElements = () => {
      const heroContainer = document.querySelector('.hero-container');
      const heroImage = document.querySelector('.hero-image');
      
      if (heroContainer && heroImage) {
        resizeObserver.observe(heroContainer);
        resizeObserver.observe(heroImage);
      }
    };

    // Check periodically for hero elements
    const checkInterval = setInterval(observeHeroElements, 100);

    // Track image loading
    const loadImages = async () => {
      const imagePromises: Promise<void>[] = [];
      
      // Get all images on the page
      const images = document.querySelectorAll('img');
      
      images.forEach(img => {
        if (!img.complete) {
          imagePromises.push(new Promise<void>(resolve => {
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Handle errors gracefully
          }));
        }
      });

      // Add icon preloading promises
      iconsToPreload.forEach(iconUrl => {
        const img = new Image();
        imagePromises.push(new Promise<void>(resolve => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = iconUrl;
        }));
      });

      await Promise.all(imagePromises);
      setImagesLoaded(true);
    };

    // Ensure minimum loading time of 500ms
    const minimumLoadingTimer = setTimeout(() => {
      setMinimumTimeElapsed(true);
    }, 500);

    // Maximum loading time of 1.3 seconds
    const maximumLoadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1300);

    loadImages();

    // Cleanup
    return () => {
      clearTimeout(minimumLoadingTimer);
      clearTimeout(maximumLoadingTimer);
      clearInterval(checkInterval);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  // Only hide loading screen when all conditions are met
  useEffect(() => {
    if (
      fontsLoaded && 
      imagesLoaded && 
      minimumTimeElapsed && 
      elementsStable && 
      dynamicImagesLoaded
    ) {
      // Add small delay to ensure smooth transition
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  }, [fontsLoaded, imagesLoaded, minimumTimeElapsed, elementsStable, dynamicImagesLoaded]);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white fixed top-0 left-0 z-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Suspense fallback={
            <div className="h-screen w-screen flex items-center justify-center bg-white">
              <LoadingSpinner />
            </div>
          }>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/request-password-reset" element={<RequestPasswordReset />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/" element={<Index />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/inbox" element={
                <ProtectedRoute>
                  <Inbox />
                </ProtectedRoute>
              } />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute adminOnly>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <UserDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route path="/VBH-login" element={<VBHLogin />} />
              <Route path="/VBH" element={<VBHDashboard />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
